/**
 * ui/components/shell/app-root.ts
 *
 * Router shell. Owns:
 * - @lit-labs/router outlet with lazy-loaded page imports
 * - Global wheel/context-menu capture
 * - No application state
 */
import { LitElement, html, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import { Router } from '@lit-labs/router';
import { keyed } from 'lit/directives/keyed.js';
import { tokens } from '../../styles/tokens.js';
import './update-banner.js';
import { gameExitHref } from '../../services/social.js';

@customElement('app-root')
export class AppRoot extends LitElement {
  static styles = [
    tokens,
    css`
      :host {
        display: block;
        width: 100%;
        height: 100%;
        font-family: var(--font-family);
        color: var(--color-text);
        background: var(--color-bg);
        overflow: hidden;
      }

      .app-container {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
      }
    `,
  ];

  private _routes = new Router(this, [
    {
      path: '/',
      render: () => {
        import('../../../ui/pages/main-menu-page.js');
        return html`<main-menu-page></main-menu-page>`;
      },
    },
    {
      path: '/puzzles',
      render: () => {
        import('../../../ui/pages/puzzle-browser-page.js');
        return html`<puzzle-browser-page></puzzle-browser-page>`;
      },
    },
    {
      path: '/packs/:packId',
      render: ({ packId }) => {
        import('../../../ui/pages/pack-page.js');
        return html`<pack-page .packId=${packId ?? ''}></pack-page>`;
      },
    },
    {
      path: '/puzzle/:id',
      render: ({ id }) => {
        import('../../../ui/pages/game-page.js');
        // ?back= is where leaving goes.
        const exitHref = gameExitHref(globalThis.location?.search ?? '');
        // Keyed so a different puzzle id mounts a fresh page; game-page only
        // loads its puzzle once, in firstUpdated.
        return keyed(id, html`<game-page .puzzleId=${id ?? ''} .exitHref=${exitHref}></game-page>`);
      },
    },
    {
      path: '/room-setup',
      render: () => {
        import('../../../ui/pages/room-setup-page.js');
        return html`<room-setup-page></room-setup-page>`;
      },
    },
    {
      path: '/room/:roomId',
      render: ({ roomId }) => {
        import('../../../ui/pages/room-page.js');
        return html`<room-page .roomId=${roomId}></room-page>`;
      },
    },
    {
      path: '/settings',
      render: () => {
        import('../../../ui/pages/settings-page.js');
        return html`<settings-page></settings-page>`;
      },
    },
    {
      path: '/about',
      render: () => {
        import('../../../ui/pages/about-page.js');
        return html`<about-page></about-page>`;
      },
    },
    {
      path: '/perf-check',
      render: () => {
        import('../../../ui/pages/perf-check-page.js');
        return html`<perf-check-page></perf-check-page>`;
      },
    },
    {
      path: '/editor',
      render: () => {
        import('../../../ui/pages/editor-page.js');
        return html`<editor-page></editor-page>`;
      },
    },
    {
      path: '/editor/:draftId',
      render: ({ draftId }) => {
        import('../../../ui/pages/puzzle-editor-page.js');
        return keyed(draftId, html`<puzzle-editor-page .draftId=${draftId ?? ''}></puzzle-editor-page>`);
      },
    },
    {
      path: '/editor/:draftId/play',
      render: ({ draftId }) => {
        import('../../../ui/pages/playtest-page.js');
        return keyed(`play:${draftId}`, html`<playtest-page .draftId=${draftId ?? ''}></playtest-page>`);
      },
    },
    {
      path: '/watch/:sharedId',
      render: ({ sharedId }) => {
        import('../../../ui/pages/replay-page.js');
        return keyed(`shared:${sharedId}`, html`<replay-page .sharedId=${sharedId ?? ''}></replay-page>`);
      },
    },
    {
      path: '/compare',
      render: () => {
        import('../../../ui/pages/compare-page.js');
        return keyed(globalThis.location?.search ?? '', html`<compare-page></compare-page>`);
      },
    },
    {
      path: '/account',
      render: () => {
        import('../../../ui/pages/account-page.js');
        return html`<account-page></account-page>`;
      },
    },
    {
      path: '/admin',
      render: () => {
        import('../../../ui/pages/admin-page.js');
        return html`<admin-page></admin-page>`;
      },
    },
    {
      path: '/replay/:replayId',
      render: ({ replayId }) => {
        import('../../../ui/pages/replay-page.js');
        return html`<replay-page .replayId=${replayId ?? ''}></replay-page>`;
      },
    },
    {
      path: '/history',
      render: () => {
        import('../../../ui/pages/history-page.js');
        return html`<history-page></history-page>`;
      },
    },
  ], {
    fallback: {
      render: () => {
        import('../../../ui/pages/main-menu-page.js');
        return html`<main-menu-page></main-menu-page>`;
      },
    },
  });

  connectedCallback() {
    super.connectedCallback();
    // Prevent OS-level pinch-zoom on the game area
    window.addEventListener('wheel', this._onWheel, { passive: false });
    // Prevent browser right-click context menu
    window.addEventListener('contextmenu', this._onContextMenu);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('wheel', this._onWheel);
    window.removeEventListener('contextmenu', this._onContextMenu);
  }

  private _onWheel = (e: WheelEvent) => {
    if (e.ctrlKey) e.preventDefault();
  };

  private _onContextMenu = (e: MouseEvent) => {
    e.preventDefault();
  };

  render() {
    return html`
      <div class="app-container">
        ${this._routes.outlet()}
      </div>
      <update-banner></update-banner>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-root': AppRoot;
  }
}
